"use client";

import React, { useState } from "react";
import { useSession, signOut } from "next-auth/react";
import Link from "next/link";
import { Popover, Transition } from "@headlessui/react";
import BaseHeader, { HeaderLogo, HeaderBackButton, HeaderTitle } from "./BaseHeader";
import CreditsDisplay from "@/components/CreditsDisplay";
import ButtonAccount from "@/components/ButtonAccount";
import ButtonSignin from "@/components/ButtonSignin";
import AuthModal from "@/components/AuthModal";

interface DashboardHeaderProps {
  /** Header variant - determines styling */
  variant?: "default" | "editor";
  /** For editor variant - the title to display */
  title?: string;
  /** For editor variant - custom back button href */
  backHref?: string;
  /** For default variant - custom logo href based on auth state */
  logoHref?: string;
  /** Hide credits display (e.g., for free tools) */
  hideCredits?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Extra action buttons rendered after credits (before account button) */
  extraActions?: React.ReactNode;
}

/**
 * DashboardHeader - Unified header for dashboard and editor pages
 *
 * Default variant: Logo + (Auth-dependent: Credits+Account OR Login+Signup)
 * Editor variant: BackButton + Title + Credits + Account
 */
export default function DashboardHeader({
  variant = "default",
  title,
  backHref = "/dashboard",
  logoHref,
  hideCredits = false,
  className = "",
  extraActions,
}: DashboardHeaderProps) {
  const { data: session } = useSession();
  const isAuthenticated = !!session?.user;
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Left content based on variant
  const leftContent = variant === "editor" ? (
    <HeaderBackButton href={backHref} />
  ) : (
    <HeaderLogo href={logoHref || (isAuthenticated ? "/dashboard" : "/")} />
  );

  // Right content - responsive layout for mobile vs desktop
  const rightContent = (
    <div className={`flex items-center shrink-0 ${variant === "editor" ? "space-x-2 md:space-x-4" : "gap-2 md:gap-4"}`}>
      {isAuthenticated ? (
        // Authenticated users - show credits and account
        <>
          {!hideCredits && <CreditsDisplay />}
          {extraActions}
          {/* Desktop: Full ButtonAccount, Mobile: Profile picture only (editor) or Hamburger menu (dashboard) */}
          <div className="hidden md:block">
            <ButtonAccount />
          </div>
          <div className="md:hidden">
            {variant === "editor" ? (
              <MobileAccountMenu showProfileOnly={true} />
            ) : (
              <MobileAccountMenu />
            )}
          </div>
        </>
      ) : (
        // Guest users - show login/signup buttons
        <>
          <div className="hidden md:flex md:items-center md:gap-4">
            <ButtonSignin
              text="Login"
              extraStyle="btn-ghost text-md px-5"
              onAuthRequired={() => setShowAuthModal(true)}
            />
            <ButtonSignin
              text="Sign Up"
              extraStyle="btn-primary text-md px-5"
              onAuthRequired={() => setShowAuthModal(true)}
            />
          </div>
          <div className="md:hidden">
            <ButtonSignin
              text="Login"
              extraStyle="btn-ghost btn-sm text-sm px-2"
              onAuthRequired={() => setShowAuthModal(true)}
            />
            <ButtonSignin
              text="Sign Up"
              extraStyle="btn-primary btn-sm text-sm px-3"
              onAuthRequired={() => setShowAuthModal(true)}
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <BaseHeader
        variant={variant}
        leftContent={leftContent}
        rightContent={rightContent}
        className={className}
      />

      {/* Auth Modal - triggered by Login/Signup buttons for guests */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {}} // Not used - page refreshes after auth
        callbackUrl={typeof window !== 'undefined' ? window.location.pathname : '/dashboard'}
        purchaseContext={undefined}
      />
    </>
  );
}

/**
 * Mobile hamburger menu for account actions
 */
function MobileAccountMenu({ showProfileOnly = false }: { showProfileOnly?: boolean }) {
  const { data: session } = useSession();

  const handleSignOut = () => {
    signOut({ callbackUrl: "/" });
  };

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            {showProfileOnly ? (
              /* Profile Picture Only - for editor variant */
              session?.user?.image ? (
                <img
                  src={session.user.image}
                  alt={session.user.name || session.user.email || "Account"}
                  className="w-8 h-8 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )
            ) : (
              /* Hamburger Icon - for dashboard variant */
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </Popover.Button>

          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-1"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-1"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  {session?.user?.image ? (
                    <img
                      src={session.user.image}
                      alt={session.user.name || session.user.email || "Account"}
                      className="w-8 h-8 rounded-full"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {session?.user?.name || session?.user?.email || "Account"}
                    </p>
                  </div>
                </div>
              </div>

              <Link
                href="/dashboard/account"
                className="flex items-center gap-2 hover:bg-gray-50 duration-200 py-2 px-4 w-full text-left font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account
              </Link>

              <Link
                href="/dashboard/support"
                className="flex items-center gap-2 hover:bg-gray-50 duration-200 py-2 px-4 w-full text-left font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Support
              </Link>

              <button
                className="flex items-center gap-2 hover:bg-red-50 hover:text-red-600 duration-200 py-2 px-4 w-full text-left font-medium text-sm"
                onClick={handleSignOut}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
